// Device Management System for Offline/Online Operations
import { appDb } from '../firebase.config';
import enhancedOfflineStorage from './enhancedOfflineStorage';
import { generateDeviceId, validateId } from './idGenerator';

class DeviceManager {
  constructor() {
    this.currentDevice = null;
    this.deviceId = null;
    this.userId = null;
    this.companyId = null;
    this.isOnline = navigator.onLine;
    this.deviceType = 'online'; // 'online' or 'offline'
  }

  // Initialize device management
  async init(userId, companyId) {
    try {
      console.log('🔄 Initializing device manager...', { userId, companyId });
      
      this.userId = userId; // Store userId
      this.companyId = companyId;
      
      // Generate or retrieve device ID
      this.deviceId = await this.getOrCreateDeviceId();
      console.log('✅ Device ID obtained:', this.deviceId);
      
      // Initialize offline storage with device context
      try {
        await enhancedOfflineStorage.init(this.deviceId, companyId);
        console.log('✅ Enhanced offline storage initialized successfully');
      } catch (storageError) {
        console.error('❌ Failed to initialize offline storage:', storageError);
        // Don't throw here, continue with online mode
        this.deviceType = 'online';
        return {
          deviceId: this.deviceId,
          deviceType: 'online',
          isApproved: false,
          error: 'Offline storage initialization failed'
        };
      }
      
      // Check if device is approved for offline use
      const deviceInfo = await this.getDeviceInfo();
      this.deviceType = deviceInfo?.status === 'approved' ? 'offline' : 'online';
      
      // Set up network status monitoring
      this.setupNetworkMonitoring();
      
      console.log('✅ Device manager initialized successfully', {
        deviceId: this.deviceId,
        deviceType: this.deviceType,
        isApproved: deviceInfo?.status === 'approved'
      });
      
      return {
        deviceId: this.deviceId,
        deviceType: this.deviceType,
        isApproved: deviceInfo?.status === 'approved'
      };
    } catch (error) {
      console.error('❌ Device manager initialization failed:', error);
      throw error;
    }
  }

  // Get or create device ID - Stable across browser sessions
  async getOrCreateDeviceId() {
    // First check if we already have a device ID
    if (this.deviceId) {
      console.log('Using existing device ID from memory:', this.deviceId);
      return this.deviceId;
    }

    // Check localStorage for existing device ID
    let deviceId = localStorage.getItem('acctoo_device_id');

    if (!deviceId) {
      try {
        // Generate a stable device ID using multiple persistent sources
        deviceId = await this.generateStableDeviceId();
        localStorage.setItem('acctoo_device_id', deviceId);
        // Also store in IndexedDB for additional persistence
        await this.storeDeviceIdInIndexedDB(deviceId);
        console.log('Generated new stable device ID:', deviceId);
      } catch (error) {
        console.error('Error generating device ID:', error);
        // Fallback to UUID if stable generation fails
        deviceId = this.generateFallbackDeviceId();
        localStorage.setItem('acctoo_device_id', deviceId);
        await this.storeDeviceIdInIndexedDB(deviceId);
        console.log('Using fallback device ID:', deviceId);
      }
    } else {
      console.log('Using existing device ID from localStorage:', deviceId);
      // Verify the device ID is still valid
      if (!this.isValidDeviceId(deviceId)) {
        console.log('Invalid device ID found, generating new one');
        deviceId = await this.generateStableDeviceId();
        localStorage.setItem('acctoo_device_id', deviceId);
        await this.storeDeviceIdInIndexedDB(deviceId);
      } else {
        // Check IndexedDB as well for consistency
        const indexedDBId = await this.getDeviceIdFromIndexedDB();
        if (indexedDBId && indexedDBId !== deviceId) {
          console.log('Device ID mismatch between localStorage and IndexedDB, using IndexedDB version');
          deviceId = indexedDBId;
          localStorage.setItem('acctoo_device_id', deviceId);
        }
      }
    }

    this.deviceId = deviceId;
    return deviceId;
  }

  // Generate stable device ID using multiple persistent sources
  async generateStableDeviceId() {
    try {
      // Use the new ID generator for consistent format
      const deviceId = generateDeviceId('STABLE_DEVICE');

      console.log('Generated stable device ID using ID generator:', deviceId);

      return deviceId;
    } catch (error) {
      console.error('Error generating stable device ID:', error);
      throw error;
    }
  }

  // Store device ID in IndexedDB for additional persistence
  async storeDeviceIdInIndexedDB(deviceId) {
    try {
      const dbName = 'ACCTOO_DEVICE_DB';
      const request = indexedDB.open(dbName, 1);

      return new Promise((resolve, reject) => {
        request.onerror = () => reject(request.error);
        request.onsuccess = (event) => {
          const db = event.target.result;
          const transaction = db.transaction(['device_info'], 'readwrite');
          const store = transaction.objectStore('device_info');
          const putRequest = store.put({ id: 'device_id', value: deviceId, timestamp: new Date() });

          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('device_info')) {
            db.createObjectStore('device_info', { keyPath: 'id' });
          }
        };
      });
    } catch (error) {
      console.error('Error storing device ID in IndexedDB:', error);
      // Don't throw error here, just log it
    }
  }

  // Get device ID from IndexedDB
  async getDeviceIdFromIndexedDB() {
    try {
      const dbName = 'ACCTOO_DEVICE_DB';
      const request = indexedDB.open(dbName, 1);

      return new Promise((resolve, reject) => {
        request.onerror = () => reject(request.error);
        request.onsuccess = (event) => {
          const db = event.target.result;
          const transaction = db.transaction(['device_info'], 'readonly');
          const store = transaction.objectStore('device_info');
          const getRequest = store.get('device_id');

          getRequest.onsuccess = () => {
            const result = getRequest.result;
            resolve(result ? result.value : null);
          };
          getRequest.onerror = () => reject(getRequest.error);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('device_info')) {
            db.createObjectStore('device_info', { keyPath: 'id' });
          }
        };
      });
    } catch (error) {
      console.error('Error getting device ID from IndexedDB:', error);
      return null;
    }
  }

  // Validate device ID format
  isValidDeviceId(deviceId) {
    if (!deviceId || typeof deviceId !== 'string') return false;
    
    // Use the new ID generator validation
    return validateId(deviceId, 'STABLE_DEVICE') || 
           validateId(deviceId, 'DEVICE') || 
           validateId(deviceId, 'FALLBACK_DEVICE');
  }

  // Fallback device ID generation (UUID-based)
  generateFallbackDeviceId() {
    // Generate a UUID-like ID as fallback
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 9);
    const fallbackId = `FALLBACK_${timestamp}_${random}`.toUpperCase();
    console.log('Generated fallback device ID:', fallbackId);
    return fallbackId;
  }

  // Simple UUID-based device ID (most stable)
  generateSimpleDeviceId() {
    // Generate a simple UUID v4
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    
    const deviceId = `SIMPLE_${uuid.toUpperCase()}`;
    console.log('Generated simple device ID:', deviceId);
    return deviceId;
  }

  // Generate unique device ID based on comprehensive hardware fingerprint
  generateDeviceId() {
    // Collect comprehensive hardware and browser characteristics
    const fingerprint = {
      // Hardware characteristics
      screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
      pixelRatio: window.devicePixelRatio,
      hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
      platform: navigator.platform,
      
      // Browser characteristics
    userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages ? navigator.languages.join(',') : '',
      
      // Timezone (stable)
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      
      // Additional stable characteristics
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack || 'unknown',
      maxTouchPoints: navigator.maxTouchPoints || 0,
      
      // Screen details
      availScreen: `${window.screen.availWidth}x${window.screen.availHeight}`,
      screenOrientation: window.screen.orientation ? window.screen.orientation.type : 'unknown',
      
      // Memory info (if available)
      memory: navigator.deviceMemory || 'unknown',
      
      // Connection info (if available)
      connection: navigator.connection ? navigator.connection.effectiveType : 'unknown'
    };
    
    // Create a stable fingerprint string
    const fingerprintString = Object.entries(fingerprint)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}:${value}`)
      .join('|');
    
    // Create a hash of the fingerprint
    let hash = 0;
    for (let i = 0; i < fingerprintString.length; i++) {
      const char = fingerprintString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    const deviceId = `DESKTOP_${Math.abs(hash).toString(36).toUpperCase()}`;
    console.log('Generated comprehensive device ID:', deviceId);
    console.log('Fingerprint details:', fingerprint);
    console.log('Fingerprint string:', fingerprintString);
    
    return deviceId;
  }

  // Register device with server
  async registerDevice(deviceId) {
    // Ensure we have a valid device ID
    if (!deviceId) {
      deviceId = await this.getOrCreateDeviceId();
    }
    
    // Ensure we have userId
    if (!this.userId) {
      return { success: false, error: 'User ID not available' };
    }
    
    const deviceInfo = {
      deviceId: deviceId,
      userId: this.userId,
      name: this.getDeviceName(),
      type: this.getDeviceType(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      status: 'pending_approval',
      createdAt: new Date(),
      lastSeen: new Date()
    };

    console.log('Registering device with complete info:', deviceInfo);

    try {
      const result = await appDb.registerDevice(deviceInfo, this.companyId);
      if (result.success) {
        this.currentDevice = deviceInfo;
        return result;
      }
      return { success: false, error: result.error };
    } catch (error) {
      console.error('Error registering device:', error);
      return { success: false, error: error.message };
    }
  }

  // Get device information
  async getDeviceInfo() {
    if (!this.deviceId || !this.companyId) return null;
    
    try {
      const deviceInfo = await appDb.getDeviceInfo(this.deviceId, this.companyId);
      this.currentDevice = deviceInfo;
      return deviceInfo;
    } catch (error) {
      console.error('Error fetching device info:', error);
      return null;
    }
  }

  // Get device name
  getDeviceName() {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Mobile')) {
      return 'Mobile Device';
    } else if (userAgent.includes('Tablet')) {
      return 'Tablet Device';
    } else if (userAgent.includes('Windows')) {
      return 'Windows Desktop';
    } else if (userAgent.includes('Mac')) {
      return 'Mac Desktop';
    } else if (userAgent.includes('Linux')) {
      return 'Linux Desktop';
    } else {
      return 'Unknown Device';
    }
  }

  // Get device type
  getDeviceType() {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Mobile')) {
      return 'mobile';
    } else if (userAgent.includes('Tablet')) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  // Check if device is approved for offline use
  async isDeviceApproved() {
    const deviceInfo = await this.getDeviceInfo();
    return deviceInfo?.status === 'approved';
  }

  // Request device approval
  async requestApproval() {
    if (!this.deviceId) return { success: false, error: 'Device not initialized' };
    
    try {
      const result = await appDb.updateDeviceStatus(this.deviceId, 'pending_approval');
      return result;
    } catch (error) {
      console.error('Error requesting approval:', error);
      return { success: false, error: error.message };
    }
  }

  // Get company devices
  async getCompanyDevices() {
    if (!this.companyId) return [];
    
    try {
      const devices = await appDb.getCompanyDevices(this.companyId);
      return devices;
    } catch (error) {
      console.error('Error fetching company devices:', error);
      return [];
    }
  }

  // Generate device-specific series number
  async generateSeriesNumber(seriesType = 'invoice') {
    if (this.deviceType === 'offline') {
      return await enhancedOfflineStorage.getNextSeriesNumber(seriesType);
    } else {
      // Online devices use server-generated numbers
      return await this.getServerSeriesNumber(seriesType);
    }
  }

  // Get server-generated series number
  async getServerSeriesNumber(seriesType) {
    // This would call server API to get next number
    // For now, return a mock number
    return Math.floor(Math.random() * 10000) + 1;
  }

  // Generate invoice number based on device type
  async generateInvoiceNumber() {
    if (this.deviceType === 'offline') {
      return await enhancedOfflineStorage.generateInvoiceNumber();
    } else {
      const seriesNumber = await this.getServerSeriesNumber('invoice');
      const year = new Date().getFullYear().toString().slice(-2);
      const nextYear = (parseInt(year) + 1).toString().slice(-2);
      return `INV${year}-${nextYear}/ON/${seriesNumber.toString().padStart(4, '0')}`;
    }
  }

  // Check if operation is allowed offline
  isOperationAllowedOffline(operation) {
    const offlineAllowedOperations = [
      'create_invoice',
      'edit_invoice',
      'create_customer',
      'edit_customer',
      'create_payment',
      'edit_payment',
      'create_item',
      'edit_item',
      'view_reports',
      'print_documents'
    ];

    const onlineOnlyOperations = [
      'add_employee',
      'remove_employee',
      'change_permissions',
      'update_company_settings',
      'change_plan',
      'add_device',
      'remove_device',
      'approve_device',
      'audit_reports'
    ];

    if (this.deviceType === 'online') {
      return true; // Online devices can do everything
    }

    if (onlineOnlyOperations.includes(operation)) {
      return false; // These operations require online mode
    }

    return offlineAllowedOperations.includes(operation);
  }

  // Set up network monitoring
  setupNetworkMonitoring() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.handleOnlineStatus();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.handleOfflineStatus();
    });
  }

  // Handle online status
  async handleOnlineStatus() {
    console.log('🌐 Device is back online');
    
    if (this.deviceType === 'offline') {
      // Sync pending data
      await this.syncPendingData();
    }
  }

  // Handle offline status
  async handleOfflineStatus() {
    console.log('📴 Device is offline');
    
    if (this.deviceType === 'online') {
      // Switch to offline mode if device is approved
      const isApproved = await this.isDeviceApproved();
      if (isApproved) {
        this.deviceType = 'offline';
        console.log('🔄 Switched to offline mode');
      }
    }
  }

  // Sync pending data
  async syncPendingData() {
    try {
      const pendingItems = await enhancedOfflineStorage.getSyncQueue({ status: 'pending' });
      
      for (const item of pendingItems) {
        await this.syncItem(item);
      }
      
      console.log(`✅ Synced ${pendingItems.length} pending items`);
    } catch (error) {
      console.error('Error syncing pending data:', error);
    }
  }

  // Sync individual item
  async syncItem(item) {
    try {
      // This would implement the actual sync logic
      // For now, just mark as synced
      await enhancedOfflineStorage.updateSyncStatus(item.id, 'synced');
      console.log(`✅ Synced item: ${item.type}`);
    } catch (error) {
      console.error('Error syncing item:', error);
      await enhancedOfflineStorage.updateSyncStatus(item.id, 'failed', error.message);
    }
  }

  // Get device status for UI
  getDeviceStatus() {
    return {
      deviceId: this.deviceId,
      deviceType: this.deviceType,
      isOnline: this.isOnline,
      isApproved: this.currentDevice?.status === 'approved',
      deviceName: this.getDeviceName(),
      lastSeen: this.currentDevice?.lastSeen
    };
  }

  // Update device last seen
  async updateLastSeen() {
    if (this.deviceId) {
      try {
        await appDb.updateDeviceStatus(this.deviceId, 'active');
        this.currentDevice.lastSeen = new Date();
      } catch (error) {
        console.error('Error updating last seen:', error);
      }
    }
  }

  // Cleanup on logout
  async cleanup() {
    await this.updateLastSeen();
    this.deviceId = null;
    this.companyId = null;
    this.currentDevice = null;
  }

  // Additional functions for Settings component
  async registerCurrentDevice() {
    return await this.registerDevice(this.deviceId);
  }

  async listUserDevices() {
    return await this.getCompanyDevices();
  }

  async removeDevice(deviceId) {
    try {
      const result = await appDb.updateDeviceStatus(deviceId, 'blocked');
      return result;
    } catch (error) {
      console.error('Error removing device:', error);
      return { success: false, error: error.message };
    }
  }

}

// Create singleton instance
const deviceManager = new DeviceManager();

// Export individual functions for Settings component
export const registerCurrentDevice = () => deviceManager.registerCurrentDevice();
export const listUserDevices = () => deviceManager.listUserDevices();
export const removeDevice = (deviceId) => deviceManager.removeDevice(deviceId);
export const getOrCreateDeviceId = () => deviceManager.getOrCreateDeviceId();

export default deviceManager;